import { ZONES, regionInZone } from '../types';

/** Country per region. Unknown regions get no country and are treated
 *  conservatively (excluded) by country-level residency checks. */
export const REGION_COUNTRY: Record<string, string> = {
  'af-south-1': 'South Africa',
  'ap-east-1': 'Hong Kong',
  'ap-east-2': 'Taiwan',
  'ap-northeast-1': 'Japan',
  'ap-northeast-2': 'South Korea',
  'ap-northeast-3': 'Japan',
  'ap-south-1': 'India',
  'ap-south-2': 'India',
  'ap-southeast-1': 'Singapore',
  'ap-southeast-2': 'Australia',
  'ap-southeast-3': 'Indonesia',
  'ap-southeast-4': 'Australia',
  'ap-southeast-5': 'Malaysia',
  'ap-southeast-6': 'New Zealand',
  'ap-southeast-7': 'Thailand',
  'ca-central-1': 'Canada',
  'ca-west-1': 'Canada',
  'eu-central-1': 'Germany',
  'eu-central-2': 'Switzerland',
  'eu-north-1': 'Sweden',
  'eu-south-1': 'Italy',
  'eu-south-2': 'Spain',
  'eu-west-1': 'Ireland',
  'eu-west-2': 'United Kingdom',
  'eu-west-3': 'France',
  'il-central-1': 'Israel',
  'me-central-1': 'UAE',
  'me-south-1': 'Bahrain',
  'mx-central-1': 'Mexico',
  'sa-east-1': 'Brazil',
  'us-east-1': 'United States',
  'us-east-2': 'United States',
  'us-west-1': 'United States',
  'us-west-2': 'United States',

  // Azure — source: https://learn.microsoft.com/en-us/azure/reliability/regions-list
  'australiacentral': 'Australia',
  'australiacentral2': 'Australia',
  'australiaeast': 'Australia',
  'australiasoutheast': 'Australia',
  'austriaeast': 'Austria',
  'belgiumcentral': 'Belgium',
  'brazilsouth': 'Brazil',
  'brazilsoutheast': 'Brazil',
  'canadacentral': 'Canada',
  'canadaeast': 'Canada',
  'centralindia': 'India',
  'centralus': 'United States',
  'chilecentral': 'Chile',
  'denmarkeast': 'Denmark',
  'eastasia': 'Hong Kong',
  'eastus': 'United States',
  'eastus2': 'United States',
  'francecentral': 'France',
  'francesouth': 'France',
  'germanynorth': 'Germany',
  'germanywestcentral': 'Germany',
  'indiasouthcentral': 'India',
  'indonesiacentral': 'Indonesia',
  'israelcentral': 'Israel',
  'italynorth': 'Italy',
  'japaneast': 'Japan',
  'japanwest': 'Japan',
  'koreacentral': 'South Korea',
  'koreasouth': 'South Korea',
  'malaysiawest': 'Malaysia',
  'mexicocentral': 'Mexico',
  'newzealandnorth': 'New Zealand',
  'northcentralus': 'United States',
  'northeurope': 'Ireland',
  'norwayeast': 'Norway',
  'norwaywest': 'Norway',
  'polandcentral': 'Poland',
  'qatarcentral': 'Qatar',
  'southafricanorth': 'South Africa',
  'southafricawest': 'South Africa',
  'southcentralus': 'United States',
  'southeastasia': 'Singapore',
  'southindia': 'India',
  'spaincentral': 'Spain',
  'swedencentral': 'Sweden',
  'switzerlandnorth': 'Switzerland',
  'switzerlandwest': 'Switzerland',
  'uaecentral': 'UAE',
  'uaenorth': 'UAE',
  'uksouth': 'United Kingdom',
  'ukwest': 'United Kingdom',
  'westcentralus': 'United States',
  'westeurope': 'Netherlands',
  'westindia': 'India',
  'westus': 'United States',
  'westus2': 'United States',
  'westus3': 'United States',
};

export function countryOf(region: string): string | null {
  return REGION_COUNTRY[region] ?? null;
}

// A "location selector" is either a literal (cloud-specific) region ID, or
// a synthetic "every region in this country, any cloud" selector. The
// prefix can't collide with a real region ID — no AWS or Azure region name
// contains a colon.
const COUNTRY_PREFIX = 'country:';

export function countrySelector(country: string): string {
  return `${COUNTRY_PREFIX}${country}`;
}

export function countryFromSelector(selector: string): string | null {
  return selector.startsWith(COUNTRY_PREFIX) ? selector.slice(COUNTRY_PREFIX.length) : null;
}

export function regionsInCountry(country: string, allRegions: string[]): string[] {
  return allRegions.filter(r => countryOf(r) === country);
}

/** Groups regions by country, for building "all providers in this
 *  country" picker entries. Regions with no known country are omitted —
 *  there's nothing to group them under. */
export function regionsByCountry(allRegions: string[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const r of allRegions) {
    const country = countryOf(r);
    if (!country) continue;
    if (!map.has(country)) map.set(country, []);
    map.get(country)!.push(r);
  }
  return map;
}

/** Top-level geography key (us, eu, apac, …) for a region; sub-zones with a
 *  parent (e.g. au) are skipped so every region maps to exactly one geography. */
export function geographyOf(region: string): string | null {
  for (const [key, def] of Object.entries(ZONES)) {
    if (def.parent) continue;
    if (regionInZone(region, key)) return key;
  }
  return null;
}

export function geographyLabel(key: string): string {
  return ZONES[key]?.label ?? key;
}

/** How far the user allows their data to travel from the selected region. */
export type ResidencyScope = 'region' | 'country' | 'geo';

export interface ScopeOption {
  scope: ResidencyScope;
  label: string;
}

/** Scope choices for a location selector (literal region or country
 *  selector), omitting tiers that add nothing. A country selector has no
 *  "this region only" tier — picking a whole country already means "any
 *  region within it is fine" — so its tightest tier is "this country". */
export function scopeOptionsFor(selector: string, allRegions: string[]): ScopeOption[] {
  const selectedCountry = countryFromSelector(selector);
  const country = selectedCountry ?? countryOf(selector);

  const options: ScopeOption[] = selectedCountry
    ? [{ scope: 'country', label: `This country (${selectedCountry})` }]
    : [{ scope: 'region', label: 'This region only' }];

  if (!selectedCountry && country && allRegions.some(r => r !== selector && countryOf(r) === country)) {
    options.push({ scope: 'country', label: country });
  }

  // Geography is derived from a representative member region — every region
  // in a country shares one geography by construction (see ZONES).
  const representative = selectedCountry ? (regionsInCountry(selectedCountry, allRegions)[0] ?? null) : selector;
  const geo = representative ? geographyOf(representative) : null;
  if (geo) {
    const geoRegions = allRegions.filter(r => geographyOf(r) === geo);
    const countryRegions = country ? regionsInCountry(country, allRegions) : [selector];
    if (geoRegions.length > countryRegions.length) {
      options.push({ scope: 'geo', label: geographyLabel(geo) });
    }
  }
  return options;
}

/** Regions that count as "this location" for containment purposes — the
 *  selected region itself, or (for a country selector) every region within
 *  that country. Lets callers tell whether cross-region routing actually
 *  takes data outside the named location, vs. just to a different point
 *  within it (e.g. a "United States" country selector: any us.* CRI
 *  destination is itself within the US, so it never truly "leaves"). */
export function boundaryRegions(selector: string, allRegions: string[]): string[] {
  const country = countryFromSelector(selector);
  return country ? regionsInCountry(country, allRegions) : [selector];
}

/** True when every destination is inside the allowed residency scope. */
export function destinationsWithinScope(
  scope: ResidencyScope,
  source: string,
  destinations: string[],
): boolean {
  if (destinations.length === 0) return false;
  if (scope === 'region') return destinations.every(d => d === source);
  if (scope === 'country') {
    const country = countryOf(source);
    return country != null && destinations.every(d => countryOf(d) === country);
  }
  const geo = geographyOf(source);
  return geo != null && destinations.every(d => geographyOf(d) === geo);
}

// Each timezone can map to a candidate region per cloud — suggestRegion
// filters to whichever of these actually exist in the current dataset, so a
// timezone with only an AWS (or only an Azure) match still suggests one.
const TIMEZONE_REGION: Record<string, string[]> = {
  'Europe/London': ['eu-west-2', 'uksouth'],
  'Europe/Dublin': ['eu-west-1', 'northeurope'],
  'Europe/Paris': ['eu-west-3', 'francecentral'],
  'Europe/Berlin': ['eu-central-1', 'germanywestcentral'],
  'Europe/Zurich': ['eu-central-2', 'switzerlandnorth'],
  'Europe/Stockholm': ['eu-north-1', 'swedencentral'],
  'Europe/Oslo': ['eu-north-1', 'norwayeast'],
  'Europe/Copenhagen': ['eu-north-1', 'denmarkeast'],
  'Europe/Rome': ['eu-south-1', 'italynorth'],
  'Europe/Madrid': ['eu-south-2', 'spaincentral'],
  'Europe/Amsterdam': ['eu-central-1', 'westeurope'],
  'Europe/Brussels': ['eu-west-3', 'belgiumcentral'],
  'Europe/Vienna': ['austriaeast'],
  'Europe/Warsaw': ['polandcentral'],
  'America/New_York': ['us-east-1', 'eastus'],
  'America/Chicago': ['us-east-2', 'centralus'],
  'America/Denver': ['us-west-2', 'westcentralus'],
  'America/Los_Angeles': ['us-west-2', 'westus'],
  'America/Phoenix': ['westus3'],
  'America/Toronto': ['ca-central-1', 'canadacentral'],
  'America/Vancouver': ['ca-west-1'],
  'America/Halifax': ['canadaeast'],
  'America/Mexico_City': ['mx-central-1', 'mexicocentral'],
  'America/Sao_Paulo': ['sa-east-1', 'brazilsouth'],
  'America/Santiago': ['chilecentral'],
  'Asia/Tokyo': ['ap-northeast-1', 'japaneast'],
  'Asia/Osaka': ['japanwest'],
  'Asia/Seoul': ['ap-northeast-2', 'koreacentral'],
  'Asia/Kolkata': ['ap-south-1', 'centralindia'],
  'Asia/Singapore': ['ap-southeast-1', 'southeastasia'],
  'Asia/Jakarta': ['ap-southeast-3', 'indonesiacentral'],
  'Asia/Kuala_Lumpur': ['ap-southeast-5', 'malaysiawest'],
  'Asia/Hong_Kong': ['ap-east-1', 'eastasia'],
  'Asia/Jerusalem': ['il-central-1', 'israelcentral'],
  'Asia/Dubai': ['me-central-1', 'uaenorth'],
  'Asia/Bahrain': ['me-south-1'],
  'Asia/Qatar': ['qatarcentral'],
  'Africa/Johannesburg': ['af-south-1', 'southafricanorth'],
  'Australia/Sydney': ['ap-southeast-2', 'australiaeast'],
  'Australia/Melbourne': ['ap-southeast-4', 'australiasoutheast'],
  'Australia/Canberra': ['australiacentral'],
  'Pacific/Auckland': ['ap-southeast-6', 'newzealandnorth'],
};

/** Privacy-clean region suggestions from the browser's IANA time zone, one
 *  per cloud that actually has a match in the current dataset. */
export function suggestRegion(allRegions: string[]): string[] {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const candidates = TIMEZONE_REGION[tz] ?? [];
    return candidates.filter(r => allRegions.includes(r));
  } catch {
    return [];
  }
}
